/**
 * Reports & Analytics Routes
 * Routes for reports and analytics dashboard
 */

import { lazy } from "react";
import { RouteObject } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppShell } from "@/components/layout/AppShell";
import { LazyPage } from "../helpers";

// Reports
const ReportsPage = lazy(() => import("@/pages/reports/ReportsPage"));
const RevenueReportPage = lazy(() => import("@/pages/reports/RevenueReportPage"));
const BillingCollectionsReportPage = lazy(() => import("@/pages/reports/BillingCollectionsReportPage"));
const ARAgingReportPage = lazy(() => import("@/pages/reports/ARAgingReportPage"));
const CollectionsReportPage = lazy(() => import("@/pages/reports/CollectionsReportPage"));
const EnrollmentStatsPage = lazy(() => import("@/pages/reports/EnrollmentStatsPage"));
const DiscountAnalysisPage = lazy(() => import("@/pages/reports/DiscountAnalysisPage").then(m => ({ default: m.DiscountAnalysisPage })));
const FamilyStatementReportPage = lazy(() => import("@/pages/reports/FamilyStatementPage"));
const ClassRosterReportPage = lazy(() => import("@/pages/reports/ClassRosterPage"));
const AttendanceReportPage = lazy(() => import("@/pages/reports/AttendanceReportPage"));
const HifdhProgressReportPage = lazy(() => import("@/pages/reports/HifdhProgressReportPage"));
const FailedPaymentsReportPage = lazy(() => import("@/pages/reports/FailedPaymentsReportPage"));
const AutopayEnrollmentReportPage = lazy(() => import("@/pages/reports/AutopayEnrollmentReportPage"));
const ReportCardsPage = lazy(() => import("@/pages/reports/ReportCardsPage").then(m => ({ default: m.ReportCardsPage })));
const BehaviorIncidentsPage = lazy(() => import("@/pages/behavior/BehaviorIncidentsPage").then(m => ({ default: m.BehaviorIncidentsPage })));
const MessagingPage = lazy(() => import("@/pages/messaging/MessagingPage").then(m => ({ default: m.MessagingPage })));

// Analytics
const AnalyticsDashboardPage = lazy(() => import("@/pages/analytics").then(m => ({ default: m.AnalyticsDashboardPage })));

const adminOnly = ["super_user", "admin", "principal"] as const;
const withStaff = ["super_user", "admin", "principal", "staff"] as const;
const withTeacher = ["super_user", "admin", "principal", "staff", "teacher"] as const;
const withTeacherAndParent = [
  "super_user", "admin", "principal", "staff", "teacher", "parent",
] as const;

export const reportRoutes: RouteObject[] = [
  // Analytics Dashboard
  {
    path: "/analytics",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><AnalyticsDashboardPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Reports Hub
  {
    path: "/reports",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><ReportsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Financial Reports
  {
    path: "/reports/revenue",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><RevenueReportPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/reports/billing-collections",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><BillingCollectionsReportPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/reports/ar-aging",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><ARAgingReportPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/reports/collections",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><CollectionsReportPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/reports/discounts",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><DiscountAnalysisPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/reports/failed-payments",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><FailedPaymentsReportPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/reports/autopay-enrollment",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><AutopayEnrollmentReportPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Academic Reports
  {
    path: "/reports/enrollment",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><EnrollmentStatsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/reports/class-roster",
    element: (
      <ProtectedRoute roles={[...withTeacher]}>
        <AppShell>
          <LazyPage><ClassRosterReportPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/reports/attendance",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><AttendanceReportPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/reports/hifdh-progress",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><HifdhProgressReportPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Family Reports
  {
    path: "/reports/family-statement",
    element: (
      <ProtectedRoute roles={[...withStaff]}>
        <AppShell>
          <LazyPage><FamilyStatementReportPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Academic — all of these have parent-readable API endpoints with
  // family_has_student_in_class checks. Widen UI to match so parents can
  // view their child's report cards + behavior; teachers also get a UI
  // route for report cards (API already allowed them).
  {
    path: "/reports/report-cards",
    element: (
      <ProtectedRoute roles={[...withTeacherAndParent]}>
        <AppShell>
          <LazyPage><ReportCardsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/behavior",
    element: (
      <ProtectedRoute roles={[...withTeacherAndParent]}>
        <AppShell>
          <LazyPage><BehaviorIncidentsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/messages",
    element: (
      <ProtectedRoute>
        <AppShell>
          <LazyPage><MessagingPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
];
